// A customer's extra email addresses, under Contact info on the customer page.
// Mail from any of them matches the customer in the Customer inbox.

import { useEffect, useState } from 'react'
import { Loader2, Plus, X } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { toast } from '../../lib/shadcn/sonner'
import { useAddCustomerEmail, useGetCustomerEmails, useRemoveCustomerEmail } from '../../hooks/backend/customersPage'

interface CustomerEmail { email: string; source: 'manual' | 'inbox' | 'zoho' | 'order' | 'merge'; added_by: string | null }

const SOURCE_LABELS: Record<CustomerEmail['source'], string> = {
  manual: 'Added by hand',
  inbox: 'Linked from the Customer inbox',
  zoho: 'A contact person in Zoho Books. Removing it here doesn’t remove it in Zoho, so the next sync may add it back.',
  order: 'From an order’s ship-to email',
  merge: 'From a customer merged into this one',
}

export default function CustomerOtherEmails({ customerId }: { customerId: string }) {
  const getEmails = useGetCustomerEmails()
  const addEmail = useAddCustomerEmail()
  const removeEmail = useRemoveCustomerEmail()
  const [emails, setEmails] = useState<CustomerEmail[] | null>(null)
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let alive = true
    setEmails(null)
    getEmails.trigger({ customer_id: customerId }, { skipCache: true })
      .then(r => { if (alive) setEmails((r as { emails: CustomerEmail[] }).emails) })
      .catch(() => { if (alive) setEmails([]) })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId])

  const save = async () => {
    setBusy(true)
    try {
      const result = (await addEmail.trigger({ customer_id: customerId, email: draft })) as { emails: CustomerEmail[]; added: boolean; email: string }
      setEmails(result.emails)
      toast.success(result.added ? `Added ${result.email}` : `${result.email} is already on this customer`)
      setDraft('')
      setAdding(false)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const remove = async (email: string) => {
    setBusy(true)
    try {
      const result = (await removeEmail.trigger({ customer_id: customerId, email })) as { emails: CustomerEmail[] }
      setEmails(result.emails)
      toast.success(`Removed ${email}`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="grid grid-cols-[80px_1fr] gap-2 items-start text-sm py-1">
      <span className="flex items-center gap-1.5 text-muted-foreground"><span className="w-4 shrink-0" />Other</span>
      <div className="min-w-0">
        {emails === null ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground mt-0.5" />
        ) : (
          <ul className="space-y-0.5">
            {emails.map(e => (
              <li key={e.email} className="flex items-center justify-between gap-2">
                <a href={`mailto:${e.email}`} className="text-primary hover:underline break-all"
                  title={`${SOURCE_LABELS[e.source]}${e.added_by ? ` (${e.added_by})` : ''}`}>{e.email}</a>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0" disabled={busy}
                  aria-label={`Remove ${e.email}`} title="Remove" onClick={() => void remove(e.email)}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
        {adding ? (
          <form className="flex gap-1.5 mt-1" onSubmit={e => { e.preventDefault(); void save() }}>
            <Input type="email" autoFocus className="h-8 text-sm" placeholder="name@example.com" aria-label="Email address to add"
              value={draft} onChange={e => setDraft(e.target.value)} />
            <Button type="submit" size="sm" className="h-8" disabled={busy || !draft.trim()}>Save</Button>
            <Button type="button" size="sm" variant="ghost" className="h-8 px-2" onClick={() => { setAdding(false); setDraft('') }}>Cancel</Button>
          </form>
        ) : emails !== null && (
          <Button variant="ghost" size="sm" className="h-6 px-1.5 -ml-1.5 text-xs text-muted-foreground"
            title="Mail from these addresses matches this customer in the Customer inbox" onClick={() => setAdding(true)}>
            <Plus className="w-3.5 h-3.5 mr-0.5" /> Add email
          </Button>
        )}
      </div>
    </div>
  )
}
